import { CSSProperties, ReactNode } from 'react';
import cn from 'classnames';
import RadioGroupContextProvider from './RadioGroupContextProvider';
import { FormError } from '@creditflowresearch/uilib/Form/FormError';
import type { IProps as IFormErrorProps } from '@creditflowresearch/uilib/Form/FormError/FormError';
import { FormField } from '@creditflowresearch/uilib/Form/FormField';
import formStyles from '../shared/form.module.scss';
import styles from './radio.module.scss';

type BaseSelectedValue = string | number | boolean;

/**
 * Props for a generic form component.
 */
export interface RadioGroupProps<T = BaseSelectedValue> {
  /**
   * The selected value of the component
   */
  selectedValue: T,
  /**
   * The child elements to be rendered inside the component
   */
  children: ReactNode,
  /**
   * The name attribute for the component
   */
  name?: string,
  /**
   * The label text or React node to be displayed with the component
   */
  label?: string | ReactNode,

  /**
   * Set the direction of the radio buttons.
   *  */
  direction?: 'vertical' | 'horizontal',
  /**
   * Metadata for form error handling
   */
  meta?: IFormErrorProps,
  /**
   * CSS class name for styling the field container
   */
  fieldClassName?: string,
  /**
   * Whether the component is disabled
   */
  disabled?: boolean,
  /**
   * Unique identifier for testing purposes
   */
  dataTest?: string,
  /**
   * Callback function triggered when the selected value changes
   */
  onChange?: (selectedValue: T) => void,
  /**
   * CSS class name for custom styling
   */
  className?: string,
  /**
   * Subtitle text to be displayed below the main label
   */
  subTitle?: string,
  /**
   * CSS class name for the label element
   */
  labelCls?: string,
  /**
   * Whether the component should be displayed in a narrow layout
   */
  isNarrow?: boolean,
  /**
   * Whether to display an error message
   */
  isErrorMessage?: boolean,
  /**
   * Gap between radio buttons, in pixels
   */
  gap?: number,
}

const RadioGroup = <T = BaseSelectedValue>({
  name,
  label,
  labelCls,
  className,
  meta,
  isErrorMessage,
  subTitle,
  isNarrow = true,
  fieldClassName,
  disabled,
  dataTest,
  selectedValue,
  onChange,
  children,
  direction,
  gap = 10,
}: RadioGroupProps<T>) => {
  return (
    <RadioGroupContextProvider<T>
      name={ name }
      disabled={ disabled }
      isErrorMessage={ isErrorMessage }
      selectedValue={ selectedValue }
      onChange={ onChange }
      meta={ meta }
    >
      <FormField
        className={ fieldClassName }
        isNarrow={ isNarrow }
        isValidationFeedback={ !!(meta?.touched && (meta?.error || meta?.warning)) }
        dataTest={ dataTest }
      >
        <div
          className={ cn(styles.formRadioGroup, className, { [styles.formRadioGroupVertical]: direction === 'vertical' }) }
          style={ { '--radio-group-gap': `${ gap }px` } as CSSProperties }
        >
          { label && (<div className={ cn(formStyles.formLabel, labelCls) }>{ label }</div>) }
          { subTitle && (<label className={ styles.subLabel }>{ subTitle }</label>) }
          { children }
        </div>

        { isErrorMessage && <FormError { ...meta } /> }
      </FormField>
    </RadioGroupContextProvider>
  );
};

export default RadioGroup;
